/**
 * 合作内容「属性集」字段配置元数据 —— 复用层（纯 TS，无 React/ui 依赖，可单测）。
 *
 * 从 CooperationPropertyForm.tsx 的 propertyTypeToComponentConfig / standardTypeToComponentConfig
 * 下沉而来：property_type / 标品层级 → 控件类型 + 校验规则 + 控件 props 的映射逻辑保持逐字节一致，
 * 仅新增 controlType 字段供「表格模式」按简化控件类型分派渲染（宽度、text 组件等）。
 * CooperationPropertyForm.tsx 改为消费本模块，避免折叠模式/表格模式两套映射逻辑分叉。
 */
import type {
  PropertyRefDto,
  TemplateStandardItemDto,
} from '@mpc/common/src/services/api';
import { getRequiredRule, getOptionsRule, getLogicOptions, getRequiredFlag } from '#features/utils';

/** 表格模式简化控件类型：由 componentClass（+ datePicker 的 range）映射而来。 */
export type PropertyControlType =
  | 'input'
  | 'select'
  | 'text'
  | 'numeric'
  | 'date'
  | 'dateRange'
  | 'upload';

export interface PropertyFieldConfig {
  /** 简化控件类型，供表格模式按类型分派渲染控件 */
  controlType: PropertyControlType;
  /** 原 FormItemDescriptions componentClass，折叠模式沿用 */
  componentClass: string;
  size?: string;
  /** 2：跨列（宽列）；未设置视为默认（1） */
  span?: number;
  /** 已合并必填规则的最终 rules（与 CooperationPropertyForm 原顺序一致：required 在前） */
  rules: any[];
  elementProps: Record<string, any>;
  valueFormat?: {
    valueToField: (value: any, fieldProps: any) => any;
    fieldToValue: (fieldValue: any, fieldProps: any) => any;
  };
  /**
   * 标品层级独有：是否必填（未合并进 rules，与 CooperationPropertyForm 原
   * standardTypeToComponentConfig 一致 —— 交给 SFormItem 的 required prop 自行处理，
   * 而非在此处拼装 required 规则）。动态属性分支的必填已经通过 getRequiredRule 合并进 rules，
   * 此字段固定为 undefined。
   */
  required?: boolean;
}

/** componentClass（+ elementProps.range）→ 表格模式简化控件类型 */
const componentClassToControlType = (
  componentClass: string,
  elementProps?: Record<string, any>,
): PropertyControlType => {
  switch (componentClass) {
    case 'select':
      return 'select';
    case 'textarea':
      return 'text';
    case 'inputNumber':
      return 'numeric';
    case 'datePicker':
      return elementProps?.range ? 'dateRange' : 'date';
    case 'text_with_upload':
      return 'upload';
    case 'input':
    default:
      return 'input';
  }
};

/**
 * 根据 property_type 生成字段配置（逻辑与 CooperationPropertyForm 原 propertyTypeToComponentConfig
 * 逐字节一致，仅下沉为独立函数 + 附加 controlType）。
 */
export const getPropertyFieldConfig = (
  schemaItem: PropertyRefDto,
  formValues?: Record<string, any[]>,
  structuredVersion?: number,
): PropertyFieldConfig => {
  const options =
    structuredVersion < 1
      ? schemaItem?.property_options?.map((v) => ({
          name: v,
          value: v,
        }))
      : getLogicOptions(schemaItem as any, formValues);

  let config: Record<string, any> = {};
  switch (schemaItem.property_type) {
    case 'text':
      config = {
        componentClass: 'textarea',
        size: 'medium',
        span: 2,
        rules: [
          {
            validator: (rule, value, callback) => {
              if (value?.length > 500) {
                callback('不能输入超过500个字符');
              } else {
                callback();
              }
            },
          },
        ],
        elementProps: {
          placeholder: '请输入',
          maxLength: 500,
        },
      };
      break;
    case 'dropdown_list':
      config = {
        componentClass: 'select',
        size: 'medium',
        rules: [...getOptionsRule(options)],
        elementProps: {
          allowClear: true,
          multiple: true,
          options,
          showComponent(value) {
            value = Array.isArray(value) ? value : [value];
            return value.join(',');
          },
          clearValueWhenOptionsEmpty: true,
        },
      };
      break;
    case 'dropdown_radio':
      config = {
        componentClass: 'select',
        size: 'medium',
        rules: [...getOptionsRule(options)],
        elementProps: {
          allowClear: true,
          multiple: false,
          options,
          showComponent(value) {
            value = Array.isArray(value) ? value : [value];
            return value.join(',');
          },
          clearValueWhenOptionsEmpty: true,
        },
      };
      break;
    case 'numeric':
      config = {
        componentClass: 'inputNumber',
        size: 'medium',
        rules: [
          {
            message: '不能输入负数',
            validator: (rule, value) => {
              if (value < 0) {
                return Promise.reject('不能输入负数');
              }
              return Promise.resolve();
            },
          },
          {
            message: '只能输入4位小数',
            validator: (rule, value) => {
              if (value && value.toString().split('.')[1]?.length > 4) {
                return Promise.reject('只能输入4位小数');
              }
              return Promise.resolve();
            },
          },
        ],
        elementProps: {
          toFixed: 4,
          placeholder: '请输入',
        },
      };
      break;
    case 'date_input':
      config = {
        componentClass: 'datePicker',
        size: 'medium',
        elementProps: {
          placeholder: '请选择日期',
        },
      };
      break;
    case 'date_range':
      config = {
        componentClass: 'datePicker',
        size: 'medium',
        span: 2,
        elementProps: {
          range: true,
          placeholder: ['开始日期', '结束日期'],
        },
      };
      break;
    case 'text_with_upload': {
      config = {
        componentClass: 'text_with_upload',
        elementProps: {},
        valueFormat: {
          /**
           * 外部value转为内部fieldValue
           * @param value 外部传入的value
           * @param fieldProps 字段属性
           * @returns 内部控件可用的fieldValue
           */
          valueToField: (value: any, fieldProps: any) => value?.[0] || {},
          /**
           * 内部fieldValue转为外部value
           * @param fieldValue 控件内部的值
           * @param fieldProps 字段属性
           * @returns 外部表单需要的value
           */
          fieldToValue: (fieldValue: any, fieldProps: any) => [fieldValue],
        },
      };
      break;
    }
    case 'input':
    default:
      config = {
        componentClass: 'input',
        size: 'medium',
        elementProps: {
          placeholder: '请输入',
          maxLength: 50,
        },
      };
  }
  const requiredRules = getRequiredRule(schemaItem as any, formValues);

  return {
    controlType: componentClassToControlType(config.componentClass, config.elementProps),
    componentClass: config.componentClass,
    size: config.size,
    span: config.span,
    elementProps: config.elementProps,
    valueFormat: config.valueFormat,
    rules: [...requiredRules, ...(config?.rules || [])],
  };
};

/**
 * 标品层级（standardCode*）字段配置（逻辑与 CooperationPropertyForm 原
 * standardTypeToComponentConfig 逐字节一致，仅下沉为独立函数 + 附加 controlType）。
 * 级联依赖 getLogicOptions 按 code 前缀 `standardCode` 识别 + row 中上级 code 过滤。
 */
export const getStandardFieldConfig = (
  schemaItem: TemplateStandardItemDto,
  formValues?: Record<string, any>,
): PropertyFieldConfig => {
  const options = getLogicOptions(schemaItem as any, formValues);

  const elementProps = {
    multiple: false,
    allowClear: true,
    options,
    showComponent(v) {
      const value = Array.isArray(v) ? v : [v];
      const opts = schemaItem?.standards?.filter((si) => value.includes(si.code));
      return opts?.map((opt) => opt.name).join('、');
    },
  };

  return {
    controlType: 'select',
    componentClass: 'select',
    size: 'medium',
    elementProps,
    rules: [...getOptionsRule(options)],
    required: schemaItem?.required,
  };
};

/** 标品项判定：TemplateStandardItemDto 无 property_type，用 code + level 区分动态属性项 */
const isStandardSchemaItem = (
  schemaItem: PropertyRefDto | TemplateStandardItemDto,
): schemaItem is TemplateStandardItemDto => !('property_type' in schemaItem);

/**
 * 该属性是否为「条件级联枚举」—— 其 options 需**按行数据动态求值**。
 *
 * 两种来源（判据与 `getLogicOptions` 的求值分支一一对应）：
 *  ① **标品多级**：`code` 形如 `standardCode{N}` 且 N > 1 —— 按上级 `standardCode{N-1}` 的值
 *     过滤 `parent_code`（如「主平台报价项目」依赖「主平台官单/私单」的选择）；
 *  ② **条件分组**：`options.use_condition` 为真 —— 按 `groups[].express` 的 json 表达式
 *     对行数据求值选组。
 *
 * ⚠️ 两者都随**行**不同而选项不同，故**不提供批量编辑**：
 * 批量编辑的语义是「一个值覆盖所有行」，而级联列的合法值域逐行不同，
 * 同一次覆盖对多数行都是非法值（业务上无意义，还会写出脏数据）。
 */
export const isCascadingAttr = (schemaItem: PropertyRefDto | TemplateStandardItemDto): boolean => {
  const code = (schemaItem as any)?.code;
  if (typeof code === 'string') {
    const m = code.match(/^standardCode(\d+)/);
    if (m) return Number(m[1]) > 1;
  }
  return !!(schemaItem as any)?.options?.use_condition;
};

/**
 * 统一入口：不区分标品/动态属性，按 schemaItem 形状自动分派。
 * 供「表格模式」属性列（standards + properties 混排）统一取字段配置。
 */
export const getSchemaItemFieldConfig = (
  schemaItem: PropertyRefDto | TemplateStandardItemDto,
  formValues?: Record<string, any>,
  structuredVersion?: number,
): PropertyFieldConfig =>
  isStandardSchemaItem(schemaItem)
    ? getStandardFieldConfig(schemaItem, formValues)
    : getPropertyFieldConfig(schemaItem, formValues, structuredVersion);

/**
 * 统一必填判定（与折叠面板模式同一来源）：
 * - 标品层级（standardCode*）：schemaItem.required（折叠模式作为 SFormItem.required prop，来源一致）；
 * - 动态属性：schema 的 required_mode/required_condition（1 必填 / 2 jsonLogic 条件 / 3 不可填），
 *   经 getRequiredFlag（与 getRequiredRule 同源）求布尔。⚠️ 不能用
 *   `rules.some(r => r.required)` 判定——text_with_upload 等 validator 形态的必填规则
 *   没有 required 字面量，表格模式会漏判必填。
 * @param formValues 整行 form value map（required_mode===2 的 jsonLogic 条件按行级联求值）
 */
export const isSchemaItemRequired = (
  schemaItem: PropertyRefDto | TemplateStandardItemDto,
  formValues?: Record<string, any>,
): boolean =>
  isStandardSchemaItem(schemaItem)
    ? !!schemaItem.required
    : getRequiredFlag(schemaItem as any, formValues);
